import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { getLocale } from '@locale/index';
import { BaseURL } from './api-host';
import { GraphQueries, request as gqlReq } from '@request/index';
import {
  OriginKey,
  KeyData,
  genKey,
  FrameType,
  MessageTemplate,
  ChannelConfigMeta
} from '@interface/index';
import {
  AGENT_DEPLOY_MODE,
  PAGE_TYPE
} from '@config/index';
import { getOrgId, getAgentId, getUserId, removeTokenCookie, getOrgNameFromUrl, getAgentNameFromUrl, getAgentLangFromUrl, getStructAgentId, getLayerIdFromUrl, getLayerTypeFromUrl } from '@lib/index';
import { AGENT_REGION, AGENT_SEARCH_TYPE, GenerationLevel, GenerationTag } from './constants';
import { over } from 'lodash';
import { ExprMode } from '@component/reference-textarea';

function respResolve(res: AxiosResponse<any>) {

  if (res.headers['content-type'] === 'application/gzip') {
    if (res.status === 200) {
      return Promise.resolve(res.data);
    }
    const err = new Error(res.data);
    (err as any).data = res.data;
    throw err;
  }

  if (res.data && res.data.token) {
    res.data.data.token = res.data.token;
  }

  if (res.data && res.data.code > 0 && res.data.msg) {
    console.log('response resolving error:', res);
    const e: any = {
      code: res.data.code,
      data: res.data.data,
      message: res.data.msg
    };

    if (typeof res.data.data === 'string' &&
      res.data.msg !== 'confirm info' &&
      res.data.msg !== '确认信息') {
      e.message = res.data.data;
    }
    return Promise.reject(e);
  }

  if (res.data && res.data.data) {
    formatRes(res.data.data);
  }

  return Promise.resolve(res.data.data);
}

function respReject(error: any) {
  console.log('response error:', error);

  if (error && error.response && error.response.status === 401) {
    removeTokenCookie();
    window.location.href = '/login';

    return Promise.reject(error);
  }

  if (typeof error.data === 'string' && error.data.length > 0) {
    error.message = JSON.stringify(error.data, undefined, 4);
  } else {
    error.data = undefined;
  }
  return Promise.reject(error);
}

(axios.interceptors.response as any).handlers = [];
axios.interceptors.response.use(
  respResolve,
  respReject
);
axios.interceptors.request.use((req: AxiosRequestConfig) => {
  req.withCredentials = true;
  return req;
});

export function initAPI() {
  axios.defaults.headers.post['Content-Type'] = 'application/json';
  axios.defaults.baseURL = BaseURL;
}

function genOriginKey(data: OriginKey) {
  return {
    agent_id: data.agentId,
    user_id: data.userId,
    commit_number: data.commitNumber
  };
}

export const API = {
  common: {
    upload(files: FileList) {
      const form = new FormData();
      form.set('image', files[0]);

      return request().post(`dialogflow/tools/upload_images`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    getQuery: (query: string, structureInput?: string, initial?: boolean, tag?: string) =>
      request().post(`/dialogflow/test`, {
        query,
        initial,
        tag,
        structure_input: structureInput
      }),
    clearDemo: (id: string) => request().get(`/dialogflow/test/clear?agent_id=${id}`),
    getPreAuthCode: () => request().get('/dialogflow/tools/preauthcode'),
    getAttributeCompletedInfoById: (ids: string[], ownerId?: string) =>
      request().post(`/integrated_data/attributes`, { attribute_ids: ids, owner_id: ownerId }),
    checkFrameDependency: (hostId: string, hostType: FrameType, frameId: string) =>
      request().get(`dialogflow/dependency/frame_check?host_id=${hostId}&host_type=${hostType}&frame_id=${frameId}`),
    checkFrameDependencyBeforehand: (hostId: string, hostType: FrameType, frameId: string) =>
      request().get(`dialogflow/dependency/frame_check/beforehand?host_id=${hostId}&host_type=${hostType}&frame_id=${frameId}`),
    checkGenericTypeDependency: (id: string) =>
      request().get(`dialogflow/dependency/generic_type_check?id=${id}`),
    checkExternalTypeDependency: (id: string) =>
      request().get(`dialogflow/dependency/external_type_check?id=${id}`),
    selection: (key: string, path?: { value: string; value_type: string }[]) =>
      gqlReq(GraphQueries.selection, { key, path }),

    //  common
    create: (page: PAGE_TYPE, data: any) =>
      request().post(`dialogflow/common`, {
        page,
        body: {
          data
        }
      }),
    update: (page: PAGE_TYPE, key: KeyData, data: any, extra?: {
      overwroteKey?: KeyData;
    }) =>
      request().patch(`dialogflow/common`, {
        page,
        body: {
          data,
          key: genKey(key),
          overwrote_key: extra && extra.overwroteKey && genKey(extra.overwroteKey)
        }
      }),
    delete: (page: PAGE_TYPE, key: KeyData, extra?: {
      isOverwrite?: boolean;
    }) =>
      request().delete(`dialogflow/common`, {
        data: {
          page,
          body: {
            key: genKey(key),
            is_overwrite: extra && extra.isOverwrite
          }
        }
      }),
    revert: (page: PAGE_TYPE, key: KeyData) =>
      request().post(`dialogflow/revert/common`, {
        page,
        body: {
          key: genKey(key)
        }
      }),
    getIdeSuggestions: (
      ideId: string,
      text: string,
      line: number,
      column: number,
      lang?: string,
      typeName?: string,
      returnType?: string,
      parameters?: string,
      receiver?: string,
      methodName?: string,
      exprMode?: ExprMode,
      returnMultiValue?: boolean,
      returnNullable?: boolean
    ) =>
      request().get(`/dialogflow/lsp?ide_id=${ideId}&text=${text}&line=${line}&column=${column}&lang=${lang}&type_name=${typeName}&return_type=${returnType}&parameters=${parameters}&receiver=${receiver}&method_name=${methodName}&expr_mode=${exprMode}&return_multi_value=${returnMultiValue}&return_nullable=${returnNullable}`),
    getGenCode: ({ ideId, typeName, returnType, parameters, comment }: {
      ideId: string;
      typeName?: string;
      returnType?: string;
      parameters?: string;
      comment?: string;
    }) =>
      request().get(`/dialogflow/lsp?ide_id=${ideId}&line=${1}&column=${1}&lang=kotlin&type_name=${typeName}&return_type=${returnType}&parameters=${parameters}&generate=true&comment=${comment}`),
    //  Promise.resolve(['123']),
    endIde: (ideId: string) =>
      request().get(`/dialogflow/lsp?ide_id=${ideId}&end=true`),
    initStructUpdate: () =>
      request().post(`/dialogflow/common/init_struct_update`),
    fakeTypes: (page: number, listType: string, search?: string, category?: string) =>
      request().post(`/dialogflow/common/type`, {
        page,
        search,
        category,
        source_strategy: listType
      }),
    // gen text
    genTypeTemplates: ({ type, slots, tag }: { type: any; slots: any; tag: GenerationTag; }) =>
      request().get(`dialogflow/common/gen_type_templates?type=${JSON.stringify(type)}&slots=${JSON.stringify(slots)}&tag=${tag}`),
    genTypeExpressions: ({ type, slots, tag }: { type: any; slots: any; tag: GenerationTag; }) =>
      request().get(`dialogflow/common/gen_type_expressions?type=${JSON.stringify(type)}&slots=${JSON.stringify(slots)}&tag=${tag}`),
    genSlotTemplates: ({ type, slot, tag }: { type: any; slot: any; tag: GenerationTag; }) =>
      request().get(`dialogflow/common/gen_slot_templates?type=${JSON.stringify(type)}&slot=${JSON.stringify(slot)}&tag=${tag}`),
    genSlotExpressions: ({ type, slot, tag }: { type: any; slot: any; tag: GenerationTag; }) =>
      request().get(`dialogflow/common/gen_slot_expressions?type=${JSON.stringify(type)}&slot=${JSON.stringify(slot)}&tag=${tag}`),
  },
  org: {
    nameCheck: (orgName: string) =>
      request().get(`/org/valid?org=${orgName}`),
    create: (data: any) => request().post(`org/create`, data),
    get: (orgId?: string, orgName?: string) => gqlReq(GraphQueries.org, { org: orgId, name: orgName }),
    update: (payload: any) =>
      request().patch(`org/update`, payload),
    delete: (orgId: string) => request().delete(`org`),
    join: (data: any) => request().post(`/org/join`, data),
    invite: (email: string) =>
      request().post('/org/member/add', {
        email
      }),
    list: () =>
      gqlReq(GraphQueries.orgs),
    search: (orgName: string) => request().get(`/org/search?org_name=${orgName}`),
    members: (orgId: string) =>
      request().get(`/org/member?org_id=${orgId}`),
    outsideMembers: (orgId: string) =>
      request().get(`/org/member?org_id=${orgId}&outsider=true`),
    applicants: (orgId: string) =>
      request().get(`/org/apply_info/org_id/${orgId}`),
    membersAndApplicants(orgId: string): any {
      return axios
        .all([
          API.org.applicants(orgId),
          API.org.members(orgId),
          API.org.outsideMembers(orgId)
        ])
        .then(axios.spread((...res) => res.map(item => item)));
    },
    emailSearch: (query: string) =>
      request().get(`/org/search_email?query=${query}`),
    applyReview: (data: any) => request().post(`/org/apply_info/review`, data),
    changeMemberRole: (userId: string, role: string) =>
      request().patch(`/org/memberships/${userId}`, { role }),
    removeOrgUser: (userId: string) => request().delete(`/org/member/${userId}`),
    removeOrgCollaborator: (userId: string) => request().delete(`/org/collaborator/${userId}`),
    providerInfo: () =>
      request().get('/org/backend/info'),
    billingStatus: () =>
      request().get('/org/billing_status'),
    createPortalSession: (sessionId: string) =>
      request().post(`/org/create_portal_session?session_id=${sessionId}`),
    getPlanLevel: (orgId: string) =>
      request().get(`/org/plan_level?org_id=${orgId}`),

    getBuilderSeatSubscription: () =>
      request().get('/org/builder_seat_subscription'),
    addFunds: (amount: number) =>
      request().post('/org/add_funds', {
        amount
      }),
    getAgentSeatSubscription: () =>
      request().get('/org/agent_seat_subscription'),
    customPortal: () =>
      request().post('/org/customer_portal'),
    changeSubscriptionPeriod: (stripeSubId: string, period: string) =>
      request().post(`/org/change_subscription_period?stripe_subscription_id=${stripeSubId}&period=${period}`),
    getTokenUsageSubscription: () =>
      request().get('/org/token_usage_subscription'),
    getBalance: () =>
      request().get('/org/balance'),
  },
  agent: {
    getAgents: (search?: string, searchType?: AGENT_SEARCH_TYPE) =>
      gqlReq(GraphQueries.agents, { search, searchType: searchType || AGENT_SEARCH_TYPE.ALL }),
    createAgent: (params: any) => request().post('/dialogflow/agent', params),
    createAgentGroup: (
      orgId: string,
      orgName: string,
      isNewStruct: boolean,
      structId: string,
      structCommitNumber: string,
      agentPayload: any,
      languages: string[]
    ) => request({
      orgId,
      orgName
    }).post('/dialogflow/agent_group', {
      languages,
      region: agentPayload && agentPayload.region,
      org_id: orgId,
      new_struct: isNewStruct,
      struct_id: structId,
      struct_commit_number: structCommitNumber,
      struct_payload: agentPayload
    }),
    createAgentGroupWithOpenapiSpec: (
      orgId: string,
      orgName: string,
      isNewStruct: boolean,
      structId: string,
      structCommitNumber: string,
      agentPayload: any,
      languages: string[],
      file: any
    ) => {
      const payload = {
        languages,
        region: agentPayload && agentPayload.region,
        org_id: orgId,
        new_struct: isNewStruct,
        struct_id: structId,
        struct_commit_number: structCommitNumber,
        struct_payload: agentPayload
      };

      const formData = new FormData();
      formData.append('payload', JSON.stringify(payload));
      formData.append('file', file);

      return request({
        orgId,
        orgName
      }).post('/dialogflow/agent_group_openapi_spec', formData);
    },
    updateAgent: (key: KeyData, data: any) =>
      request().patch(`/dialogflow/agent`, { data, key: genKey(key) }),
    deleteAgent: (key: KeyData) => request().delete(`/dialogflow/agent`, {
      data: {
        key: genKey(key),
      },
      headers: { agentID: key.agentID }
    }),
    getAgentById: (id: string) =>
      gqlReq(GraphQueries.agent, {
        key: genKey({ metaID: id, agentID: id })
      }, undefined, { agentID: id }),
    getSimpleBotInfo: (id: string) =>
      request().get(`/dialogflow/agent/bot_info?id=${id}`),
    getGeneralInfo: (id: string) =>
      request().get(`/dialogflow/agent/general?id=${id}`),
    hasInternalOrPrivateModule: (id: string) =>
      request().get(`/dialogflow/agent/with_private_or_internal_module?id=${id}`),
    getProviderPayload: (id: string) =>
      request().get(`/dialogflow/agent/provider_payload?provider_id=${id}`),
    updateGeneralInfo: (id: string, info: any) =>
      request().patch(`/dialogflow/agent/general?id=${id}`, info),
    getMasterAgent: (orgName: string, agentName: string, lang: string) =>
      gqlReq(GraphQueries.masterAgent, {
        key: { lang, org_name: orgName, agent_name: agentName }
      }),
    availableParameters: (agentId: string) =>
      gqlReq(GraphQueries.availableParameters, {
        agentKey: genKey({ metaID: agentId }),
        missionInput: {
          agent_id: agentId,
          user_id: getUserId(),
          commit_number: '',
          master_base: ''
        }
      }),
    updateTemplate: (originKey: OriginKey) =>
      request().post('/dialogflow/application/update', {
        template_key: genOriginKey(originKey)
      }),
    templates: () =>
      request().get('/dialogflow/template'),
    members: () =>
      gqlReq(GraphQueries.agentAccesses, { key: genKey({ metaID: getStructAgentId() || '', agentID: getStructAgentId() }) }, 'agent.accesses'),
    addMember: (email: string) =>
      request().post('/dialogflow/agent/access', {
        email
      }),
    changeMemberRole: (userId: string, role: string) =>
      request().patch(`/dialogflow/agent/access/${userId}`, { role }),
    removeMember: (userId: string) =>
      request().delete(`/dialogflow/agent/access/${userId}`),
    libraries: (page: number, search?: string) =>
      request().get(`/dialogflow/library/public?page=${page}&search=${search}`),
    previewLibrary: (agentId: string, commitId: string, masterBase?: string) =>
      gqlReq(GraphQueries.review, {
        key: {
          meta_id: agentId,
          agent_id: agentId
        },
        missionInput: {
          user_id: 'master',
          agent_id: agentId,
          commit_number: commitId,
          master_base: masterBase || commitId
        },
        strategy: 'PLATFORM'
      }),
    importLibray: (originKey: OriginKey, hostKey?: OriginKey) =>
      request().post(`/dialogflow/library/import`, {
        origin_key: genOriginKey(originKey),
        host_key: hostKey && genOriginKey(hostKey)
      }),
    unimportLibray: (originKey: OriginKey, structId?: string) =>
      request().delete(`/dialogflow/library/import`, {
        data: {
          origin_key: genOriginKey(originKey)
        },
        headers: structId && {
          agentID: structId
        } || undefined
      }),
    updateImportedLibrary: (
      originKey: OriginKey,
      latestOriginKey: OriginKey,
      structId?: string
    ) =>
      request().patch('/dialogflow/library/import', {
        origin_key: genOriginKey(originKey),
        latest_origin_key: genOriginKey(latestOriginKey)
      }, structId && {
        headers: {
          agentID: structId
        }
      } || undefined),
    importedLibraries: (structId?: string) =>
      request().get('/dialogflow/library/imported', structId && {
        headers: {
          agentID: structId
        }
      } || undefined),
    libraryCheck: (
      action: string,
      originKey?: OriginKey,
      latestOriginKey?: OriginKey,
      hostKey?: OriginKey
    ) =>
      request().post(`/dialogflow/library/check`, {
        action,
        origin_key: originKey && genOriginKey(originKey),
        latest_origin_key: latestOriginKey && genOriginKey(latestOriginKey),
        host_key: hostKey && genOriginKey(hostKey)
      }),
    cloneableList: (page: number, search?: string, type?: AGENT_SEARCH_TYPE) =>
      request().get(`/dialogflow/cloneable/public?page=${page}&type=${type}&search=${search}`),
    clone: (originKey: OriginKey, name: string, deployMode?: AGENT_DEPLOY_MODE, region?: AGENT_REGION, toOrgId?: string) =>
      request().post(`/dialogflow/cloneable/clone`, {
        name,
        region,
        deploy_mode: deployMode,
        origin_key: genOriginKey(originKey),
        to_org_id: toOrgId
      }),
    export: (agentId?: string) =>
      request().get(`/dialogflow/agent/export`, agentId && {
        headers: {
          agentID: agentId
        }
      } || undefined),
    clearCahce: () =>
      request().delete(`/dialogflow/agent/clean_session`),
    starInfo: () =>
      request().get(`/dialogflow/agent/star`),
    starAgent: () =>
      request().post(`/dialogflow/agent/star`),
    removeAgentStar: () =>
      request().delete(`/dialogflow/agent/star`),
    getAgentReadMe: (agentId: string, lang: string) =>
      gqlReq(GraphQueries.agent, {
        key: genKey({ metaID: agentId, agentID: agentId })
      }, 'result.read_me', {
        agentID: agentId,
        agentLang: lang
      }),
    updateAgentReadMe: (key: KeyData, data: any, lang: string) =>
      request({
        agentId: key.agentID,
        agentLang: lang,
      }).patch(`/dialogflow/agent`, { data, key: genKey(key) }),
    commitAgentReadMe: (agentId: string, lang: string) => request(
      {
        agentId,
        agentLang: lang
      }).post(`/dialogflow/branch/test`),
    sendShareEmail: (url: string, to: string, code: string) =>
      request().post(`/dialogflow/agent/share`, {
        to,
        url,
        code
      })
  },
  review: {
    newPr: (agentId?: string, note?: string) =>
      request().post(`/dialogflow/branch/pull_request/new`, {
        note,
        raised_by: {
          user_id: getUserId()
        }
      },
        agentId && {
          headers: {
            agentID: agentId
          }
        } || undefined),
    prList: (agentId?: string) =>
      request().get(`/dialogflow/branch/pull_request/list`, agentId && {
        headers: {
          agentID: agentId
        }
      } || undefined),
    prReview: (agentId: string, account: string, commitId: string, opinion: string, tag?: string) =>
      request().post(`/dialogflow/branch/pull_request/review`, {
        opinion,
        tag,
        pull_request: {
          user_id: account,
          agent_id: getAgentId(),
          commit_number: commitId
        },
        reviewed_by: {
          user_id: getUserId()
        }
      }, {
        headers: {
          agentID: agentId
        }
      })
  },
  composite: {
    getItemListService: (ids: string[]) =>
      request().post('integrated_data/composite_attributes', {
        composite_attribute_ids: ids
      }),
    pagingList: (page: number, imported: boolean, search?: string) =>
      gqlReq(GraphQueries.pagingList, {
        conf: {
          page,
          imported,
          search,
          data_type: PAGE_TYPE.COMPOSITE
        }
      }, 'pagination'),
    availableParameters: (compositeId: string, agentId: string) =>
      gqlReq(GraphQueries.compositeAvaibleParameters, {
        compositeKey: genKey({ metaID: compositeId, agentID: agentId }),
        agentKey: genKey({
          metaID: getAgentId()
        })
      }),
    getComposites: (search?: string) =>
      gqlReq(GraphQueries.composites, { search, agentKey: genKey({ metaID: getAgentId() }) }, 'agent.composites'),
    getItemService: (id: string, agentId?: string) =>
      gqlReq(GraphQueries.composite, { key: genKey({ metaID: id, agentID: agentId }) }),
    createService: (data: any) =>
      API.common.create(PAGE_TYPE.COMPOSITE, data),
    updateItemService: (key: KeyData, data: any) =>
      API.common.update(PAGE_TYPE.COMPOSITE, key, data),
    deleteItemService: (key: KeyData) =>
      API.common.delete(PAGE_TYPE.COMPOSITE, key),
    revertToMaster: (key: KeyData) =>
      API.common.revert(PAGE_TYPE.COMPOSITE, key),
    availableComposites: () =>
      gqlReq(GraphQueries.availableComposites, {
        key: genKey({
          metaID: getAgentId()
        })
      }, 'agent.available_composites')
  },
  intent: {
    getIntents: (search?: string) =>
      gqlReq(GraphQueries.intents, { search, agentKey: genKey({ metaID: getAgentId() }) }, 'agent.intents'),
    pagingList: (page: number, imported: boolean, search?: string, category?: string) =>
      gqlReq(GraphQueries.pagingList, {
        conf: {
          page,
          imported,
          search,
          category,
          data_type: PAGE_TYPE.INTENT
        }
      }, 'pagination'),
    createIntent: (params: any) =>
      API.common.create(PAGE_TYPE.INTENT, params),
    updateIntent: (key: KeyData, data: any) =>
      API.common.update(PAGE_TYPE.INTENT, key, data),
    deleteIntent: (key: KeyData) =>
      API.common.delete(PAGE_TYPE.INTENT, key),
    getIntentById: (id: string) =>
      gqlReq(GraphQueries.intent, { key: genKey({ metaID: id }) }),
    availableParameters: (intentId: string) =>
      gqlReq(GraphQueries.intentAvailableParameters, {
        intentKey: genKey({ metaID: intentId }),
        agentKey: genKey({ metaID: getAgentId() })
      }),
    revertToMaster: (key: KeyData) =>
      API.common.revert(PAGE_TYPE.INTENT, key),
    availableIntents: () =>
      gqlReq(GraphQueries.availableIntents, {
        key: genKey({
          metaID: getAgentId()
        })
      }, 'agent.available_intents')
  },
  frame: {
    deadLockCheck: (data: any) =>
      request().get(`/dialogflow/frame/dead_lock_check?host_id=${data.host_id}&host_type=${data.host_type}&target_id=${data.target_id}&target_type=${data.target_type}`)
  },

  entity: {
    getEntities: (search?: string) =>
      gqlReq(GraphQueries.entities, { search, agentKey: genKey({ metaID: getAgentId() }) }, 'agent.entities'),
    pagingList: (page: number, imported: boolean, search?: string) =>
      gqlReq(GraphQueries.pagingList, {
        conf: {
          page,
          imported,
          search,
          data_type: PAGE_TYPE.ENTITY
        }
      }, 'pagination'),
    createEntity: (params: any) =>
      API.common.create(PAGE_TYPE.ENTITY, params),
    updateEntity: (key: KeyData, data: any) =>
      API.common.update(PAGE_TYPE.ENTITY, key, data),
    deleteEntity: (key: KeyData) =>
      API.common.delete(PAGE_TYPE.ENTITY, key),
    revertToMaster: (key: KeyData) =>
      API.common.revert(PAGE_TYPE.ENTITY, key),
    deleteEntityContent: (key: KeyData) =>
      API.common.delete(PAGE_TYPE.ENTITY_CONTENT, key),
    updateEntityContent: (key: KeyData, data: any) =>
      API.common.update(PAGE_TYPE.ENTITY_CONTENT, key, data),
    createEntityContent: (data: any) =>
      API.common.create(PAGE_TYPE.ENTITY_CONTENT, data),
    revertEntityContent: (key: KeyData) =>
      API.common.revert(PAGE_TYPE.ENTITY_CONTENT, key),
    getEntity: (id: string, search?: string, page?: number) =>
      gqlReq(GraphQueries.entity, { search, page, key: genKey({ metaID: id }) }),
    availableParameters: (entityId: string) =>
      gqlReq(GraphQueries.entityAvailableParameters, {
        entityKey: genKey({ metaID: entityId }),
        agentKey: genKey({ metaID: getAgentId() })
      }),
    syncDynamicData: (id: string) =>
      request().post(`/dialogflow/tools/sync_data/${id}`)
  },
  service: {
    pagingList: (page: number, imported: boolean, search?: string) =>
      gqlReq(GraphQueries.pagingList, {
        conf: {
          page,
          imported,
          search,
          data_type: PAGE_TYPE.SERVICE
        }
      }, 'pagination'),
    createService: (data: any) =>
      API.common.create(PAGE_TYPE.SERVICE, data),
    updateService: (key: KeyData, data: any) =>
      API.common.update(PAGE_TYPE.SERVICE, key, data),
    getServices: (search?: string) =>
      gqlReq(GraphQueries.services, { search, agentKey: genKey({ metaID: getAgentId() }) }, 'agent.services'),
    getService: (agentId?: string, id?: string, userId?: string) =>
      gqlReq(GraphQueries.service, {
        key: genKey({
          metaID: id || '',
          agentID: agentId || getAgentId()
        })
      }, undefined, userId && { userID: userId } || undefined),
    deleteService: (key: KeyData) =>
      API.common.delete(PAGE_TYPE.SERVICE, key),
    availableParameters: (agentId: string) =>
      gqlReq(GraphQueries.serviceAvaibleParameters, {
        agentKey: genKey({ metaID: getAgentId() })
      }),
    functionTest: (funcName: string, parameters: any) =>
      request().post(`/dialogflow/test/function?func=${funcName}`, parameters),
    functionTestWithCodeExpression: (code: string) =>
      request().post(`/dialogflow/test/function?code=${code}`)
  },
  integration: {
    list: () =>
      gqlReq(GraphQueries.integrations, {
        agentID: getAgentId()
      }),
    update: (data: any) =>
      request().post(`/dialogflow/integration`, data),
    delete: (data: any) =>
      request().delete(`/dialogflow/integration`, { data }),
    availableChannels: async () =>
      ['restful'], // Hidden for now
    // request().get(`/dialogflow/integration/on`).then((res: any)=> {
    //   const ret=res || [];
    //   if (ret.findIndex((tag: string)=> tag==='restful')===-1) {
    //     ret.unshift('restful');
    //   }
    //   return ret;
    // }),
    generateWebhook: (channel: string) =>
      request().get(`/dialogflow/integration/webhook_generation?channel=${channel}`),
    getIntegration: () => request().get(`/dialogflow/integration`),
    createIntegration: (data: any) =>
      request().post('/dialogflow/integration', data),

    replaceIntegrationData: (data: any) =>
      request().post('/dialogflow/integration/replace', data),

    // sdk integration
    getSdkPlatformInfo: () =>
      request().get('/dialogflow/integration/sdkplatform/sdk_infos'),
    getAppSecret: (id: string) =>
      request().get(`/dialogflow/integration/sdkplatform/configure_agent?app_id=${id}`)
  },
  channel: {
    update: (data: any) =>
      request().post(`/dialogflow/channel`, data),
    delete: (data: any) =>
      request().delete(`/dialogflow/channel`, { data }),
    getAll: () => request().get(`/dialogflow/channel`),
    createIntegration: (data: any) =>
      request().post('/dialogflow/integration', data)
  },
  connection: {
    getRawConnections: () =>
      gqlReq(GraphQueries.connections, { key: genKey({ metaID: getStructAgentId() }) }, 'agent.connections'),
    addConnection: (data: any) =>
      request().post('/dialogflow/connection', data),
    updateConnection: (data: any) =>
      request().patch('/dialogflow/connection', data),
    deleteConnection: (data: any) =>
      request().delete('/dialogflow/connection', { data }),
    initConfig: (url: string, body: string) =>
      request().post(url, body, { baseURL: '' })
  },
  agentResponse: {
    createAgentResponse: (data: any) =>
      API.common.create(PAGE_TYPE.RESPONSE, data),
    updateAgentResponse: (key: KeyData, data: any) =>
      API.common.update(PAGE_TYPE.RESPONSE, key, data),
    deleteAgentResponse: (key: KeyData) =>
      API.common.delete(PAGE_TYPE.RESPONSE, key),
    revertAgentResponse: (key: KeyData) =>
      API.common.revert(PAGE_TYPE.RESPONSE, key),
    getInheritEntityConditionOption: (parameters: string[]) =>
      request().get(`dialogflow/inherit_entity/parameter`, {
        params: { parameters }
      }),
    checkIntentActionDependency: (intentId: string) =>
      request().get(`dialogflow/dependency/intent_action_check?intent_id=${intentId}`)
  },
  attr: {
    getAttr: (id: string, ownerId: string, headers?: { [key: string]: any }) =>
      gqlReq(GraphQueries.attribute, { key: genKey({ metaID: id, ownerID: ownerId }) }, undefined, headers),
    createAttr: (data: any) =>
      API.common.create(PAGE_TYPE.ATTRIBUTE, data),
    updateAttr: (
      key: KeyData,
      data: any,
      overwroteKey?: KeyData
    ) =>
      API.common.update(PAGE_TYPE.ATTRIBUTE, key, data, {
        overwroteKey
      }),
    deleteAttr: (key: KeyData, isOverwrite: boolean) =>
      API.common.delete(PAGE_TYPE.ATTRIBUTE, key, { isOverwrite }),
    revertAttr: (key: KeyData) =>
      API.common.revert(PAGE_TYPE.ATTRIBUTE, key),
    positionCheck: (
      id: string,
      positions: {
        page_id: string;
        frame_id: string;
      }[]
    ) =>
      request().post('dialogflow/dependency/slot_position_check/beforehand', {
        attribute_id: id,
        slot_positions: positions
      })
  },
  user: {
    getUserInfo: () =>
      request().get(`/account/info`),
    userRegister: (data: any) => request().post('/account/register', data),
    userLogin: (data: any) => request().post('/account/login', data),
    findPassword: (data: any) =>
      request().post('/account/reset_password', data),
    sendAuthCode: (phone: string) =>
      request().get(`/account/verify?phone=${phone}`),
    updateUserInfo: (data: any) => request().post('/account/update_info', data),
    isUserExist: (phone: string, areaCode: number, mode: string) =>
      request().get(`/account/check?phone=${phone}&area_code=${areaCode}&mode=${mode}`),
    getWechatUserInfo({ code }: { code: string }) {
      return request().get(`/org/wechat/login?code=${code}`);
    },
    updateOrg: (data: any) => request().patch('/org/update', data),
    changeMemberRole: (data: any) => request().post(`/org/auth/role`, data),
    verifyEmailAddress: () =>
      request().get(`/account/send_mail_verify`),
    verifyEmailWithToken: (token: string) =>
      request().get(`/account/check_mail_verify?token=${token}`),
    githubLogin: () =>
      request().get(`/account/oauth/github/login${get3rdLoginRedirect()}`),
    googleLogin: () =>
      request().get(`/account/oauth/google/login${get3rdLoginRedirect()}`),
    linkedLogin: () =>
      request().get(`/account/oauth/linkedin/login${get3rdLoginRedirect()}`),
    configBotGoogleAuth: () =>
      request().get(`account/oauth/google/config_bot_login`),
    configBotUpdateFacebookLogin: (body: any) =>
      request().post(`webhook/facebook_update`, body)
    // configBotGoogleCallback: ()=>
    //   request().get(`account/oauth/google/config_bot_callback`)
  },
  support: {
    getSupportList: () =>
      request().get('/dialogflow/agent/support'),
    updateSupportList: (data: any) =>
      request().patch(`/dialogflow/agent/support`, data),
    getItem: (key: string) =>
      request().get(`/dialogflow/agent/support?key=${key}`)
  },
  dataQuery: {
    get: (id: string, ownerId: string) =>
      gqlReq(GraphQueries.dataQuery, { key: genKey({ metaID: id, ownerID: ownerId }) }),
    create: (data: any) =>
      API.common.create(PAGE_TYPE.CLOSURE, data),
    update: (
      data: any,
      key: KeyData,
      overwroteKey?: KeyData
    ) =>
      API.common.update(PAGE_TYPE.CLOSURE, key, data, {
        overwroteKey
      }),
    delete: (key: KeyData, isOverwrite: boolean) =>
      API.common.delete(PAGE_TYPE.CLOSURE, key, { isOverwrite }),
    revert: (key: KeyData) =>
      API.common.revert(PAGE_TYPE.CLOSURE, key)
  },
  stateUpdate: {
    get: (id: string, ownerId: string) =>
      gqlReq(GraphQueries.stateUpdate, { key: genKey({ metaID: id, ownerID: ownerId }) }),
    create: (data: any) =>
      API.common.create(PAGE_TYPE.STATE_UPDATE, data),
    update: (
      data: any,
      key: KeyData,
      overwroteKey?: KeyData
    ) =>
      API.common.update(PAGE_TYPE.STATE_UPDATE, key, data, {
        overwroteKey
      }),
    delete: (key: KeyData, isOverwrite: boolean) =>
      API.common.delete(PAGE_TYPE.STATE_UPDATE, key, { isOverwrite }),
    revert: (key: KeyData) =>
      API.common.revert(PAGE_TYPE.STATE_UPDATE, key)
  },
  frameMapping: {
    create: (data: any) =>
      API.common.create(PAGE_TYPE.FRAME_MAPPING, data),
    update: (
      data: any,
      key: KeyData,
      overwroteKey?: KeyData
    ) =>
      API.common.update(PAGE_TYPE.FRAME_MAPPING, key, data, {
        overwroteKey
      }),
    delete: (key: KeyData, isOverwrite: boolean) =>
      API.common.delete(PAGE_TYPE.FRAME_MAPPING, key, { isOverwrite }),
    revert: (key: KeyData) =>
      API.common.revert(PAGE_TYPE.FRAME_MAPPING, key)
  },
  suggestion: {
    create: (data: any) =>
      API.common.create(PAGE_TYPE.SUGGESTION, data),
    update: (
      key: KeyData,
      data: any,
      overwroteKey?: KeyData
    ) =>
      API.common.update(PAGE_TYPE.SUGGESTION, key, data, {
        overwroteKey
      }),
    delete: (key: KeyData) =>
      API.common.delete(PAGE_TYPE.SUGGESTION, key),
    revert: (key: KeyData) =>
      API.common.revert(PAGE_TYPE.SUGGESTION, key)
  },
  expression: {
    create: (data: any) =>
      API.common.create(PAGE_TYPE.PHRASE, data),
    update: (key: KeyData, data: any) =>
      API.common.update(PAGE_TYPE.PHRASE, key, data),
    delete: (key: KeyData) =>
      API.common.delete(PAGE_TYPE.PHRASE, key),
    revert: (key: KeyData) =>
      API.common.revert(PAGE_TYPE.PHRASE, key)
  },
  prompt: {
    create: (data: any) =>
      API.common.create(PAGE_TYPE.PROMPT, data),
    update: (key: KeyData, data: any) =>
      API.common.update(PAGE_TYPE.PROMPT, key, data),
    delete: (key: KeyData) =>
      API.common.delete(PAGE_TYPE.PROMPT, key),
    revert: (key: KeyData) =>
      API.common.revert(PAGE_TYPE.PROMPT, key)
  },
  companion: {
    create: (data: any) =>
      API.common.create(PAGE_TYPE.COMPANION, data),
    update: (
      key: KeyData,
      data: any,
      overwroteKey?: KeyData
    ) =>
      API.common.update(PAGE_TYPE.COMPANION,
        key,
        data,
        overwroteKey && {
          overwroteKey
        } || undefined),
    delete: (key: KeyData) =>
      API.common.delete(PAGE_TYPE.COMPANION, key),
    revert: (key: KeyData) =>
      API.common.revert(PAGE_TYPE.COMPANION, key)
  },
  version: {
    status: () => request().get(`/dialogflow/branch/status`),
    test: () => request().post(`/dialogflow/branch/test`),
    rebase: () => request().post(`/dialogflow/branch/rebase`),
    review: (agentId?: string, userId?: string, commitId?: string, masterBase?: string) =>
      gqlReq(GraphQueries.review, {
        key: {
          meta_id: getAgentId(),
          agent_id: getAgentId()
        },
        missionInput: commitId && {
          user_id: userId || getUserId(),
          agent_id: agentId || getAgentId(),
          commit_number: commitId,
          master_base: masterBase
        },
        strategy: 'REVIEW'
      }),
    conflictData: (agentId?: string) =>
      gqlReq(GraphQueries.review, {
        key: {
          meta_id: agentId || getAgentId(),
          agent_id: agentId || getAgentId()
        },
        strategy: 'CONFLICT'
      }, undefined, {
        agentID: agentId || getAgentId()
      }
      ),
    resolveConflicts: (resolvedList: any, agentId?: string) =>
      request().post(`/dialogflow/branch/resolve`, resolvedList, {
        headers: {
          agentID: agentId || getAgentId()
        }
      }),
    revert: (to: 'master' | 'commit') =>
      request().post(`/dialogflow/branch/revert?revert_to=${to}`),
    deploy: (agentId?: string) =>
      request().post(`/dialogflow/agent/deploy`, undefined, agentId && {
        headers: {
          agentID: agentId
        }
      } || undefined),
    deployMeta: (providerType: string) => {
      return fetch(`https://${process.env.NODE_ENV ? 'host-dev' : 'host'}.opencui.io/v1/config_meta?provider_type=${providerType}`).then(res => res.json());
    },
    deployInfo: (agentId?: string) =>
      request().get(`/dialogflow/agent/deploy`, agentId && {
        headers: {
          agentID: agentId
        }
      } || undefined),
    deployHistory: (agentId?: string) =>
      request().get(`/dialogflow/agent/deploy/history`, agentId && {
        headers: {
          agentID: agentId
        }
      } || undefined),
    debugHistory: (agentId?: string) =>
      request().get(`/dialogflow/agent/deploy/debug_history`, agentId && {
        headers: {
          agentID: agentId
        }
      } || undefined),
    getLoggingInfo: () =>
      request().get(`/dialogflow/agent/deploy/logging`)
  },
  adaptation: {
    getList: (page: number, pageSize: number, operations: any[], frameIDs: string[], startDate: number, endDate: number) =>
      request().get(`dialogflow/adaptation/list?page=${page}&page_size=${pageSize}&operations=${operations}&frame_ids=${frameIDs}&start_date=${startDate}&end_date=${endDate}`),
    enable: (data: any) =>
      request().post('dialogflow/adaptation/enable', data),
    disable: (data: any) =>
      request().post('dialogflow/adaptation/disable', data),
    remove: (data: any) =>
      request().delete('dialogflow/adaptation/remove', { data })
  },
  valueCheck: {
    create: (data: any) =>
      API.common.create(PAGE_TYPE.CHECK, data),
    update: (
      key: KeyData,
      data: any,
      overwroteKey?: KeyData
    ) =>
      API.common.update(PAGE_TYPE.CHECK,
        key,
        data,
        overwroteKey && {
          overwroteKey
        } || undefined),
    delete: (key: KeyData) =>
      API.common.delete(PAGE_TYPE.CHECK, key),
    revert: (key: KeyData) =>
      API.common.revert(PAGE_TYPE.CHECK, key)
  },
  slotStorage: {
    create: (data: any) =>
      API.common.create(PAGE_TYPE.SLOT_STORAGE_ANNOTATION, data),
    update: (
      key: KeyData,
      data: any,
      overwroteKey?: KeyData
    ) =>
      API.common.update(PAGE_TYPE.SLOT_STORAGE_ANNOTATION,
        key,
        data,
        overwroteKey && {
          overwroteKey
        } || undefined),
  },
  initialization: {
    create: (data: any) =>
      API.common.create(PAGE_TYPE.INITIALIZATION, data),
    update: (
      key: KeyData,
      data: any,
      overwroteKey?: KeyData
    ) =>
      API.common.update(PAGE_TYPE.INITIALIZATION,
        key,
        data,
        overwroteKey && {
          overwroteKey
        } || undefined),
    delete: (key: KeyData) =>
      API.common.delete(PAGE_TYPE.INITIALIZATION, key),
    revert: (key: KeyData) =>
      API.common.revert(PAGE_TYPE.INITIALIZATION, key)
  },
  filtering: {
    create: (data: any) =>
      API.common.create(PAGE_TYPE.FILTERING, data),
    update: (
      key: KeyData,
      data: any,
      overwroteKey?: KeyData
    ) =>
      API.common.update(PAGE_TYPE.FILTERING,
        key,
        data,
        overwroteKey && {
          overwroteKey
        } || undefined),
    delete: (key: KeyData) =>
      API.common.delete(PAGE_TYPE.FILTERING, key),
    revert: (key: KeyData) =>
      API.common.revert(PAGE_TYPE.FILTERING, key)
  },
  dialogAct: {
    create: (data: any) =>
      API.common.create(PAGE_TYPE.DIALOG_ACT, data),
    update: (
      key: KeyData,
      data: any,
      overwriteId?: string
    ) =>
      API.common.update(PAGE_TYPE.DIALOG_ACT,
        overwriteId
          ? {
            metaID: key.metaID,
            ownerID: overwriteId
          }
          : key,
        data,
        overwriteId && {
          overwroteKey: key
        } || undefined),
    delete: (key: KeyData) =>
      API.common.delete(PAGE_TYPE.DIALOG_ACT, key),
    revert: (key: KeyData) =>
      API.common.revert(PAGE_TYPE.DIALOG_ACT, key)
  },
  postFillAction: {
    create: (data: any) =>
      API.common.create(PAGE_TYPE.POST_FILL_ACTION, data),
    update: (
      key: KeyData,
      data: any,
      overwroteKey?: KeyData
    ) =>
      API.common.update(PAGE_TYPE.POST_FILL_ACTION,
        key,
        data,
        overwroteKey && {
          overwroteKey
        } || undefined),
    delete: (key: KeyData) =>
      API.common.delete(PAGE_TYPE.POST_FILL_ACTION, key),
    revert: (key: KeyData) =>
      API.common.revert(PAGE_TYPE.POST_FILL_ACTION, key)
  },
  confirmation: {
    create: (params: any) =>
      API.common.create(PAGE_TYPE.CONFIRMATION, params),
    update: (key: KeyData, data: any, overwroteKey?: KeyData) =>
      API.common.update(PAGE_TYPE.CONFIRMATION, key, data, { overwroteKey }),
    delete: (key: KeyData) =>
      API.common.delete(PAGE_TYPE.CONFIRMATION, key),
    revert: (key: KeyData) =>
      API.common.revert(PAGE_TYPE.CONFIRMATION, key)
  },
  struct: {
    createStructAgent: (agentId: string) =>
      request().post(`/dialogflow/branch/struct/form?agent_id=${agentId}`),
    createSubAgent: (agentId: string, lang: string) =>
      request().post(`/dialogflow/branch/struct/form_lang?agent_id=${agentId}&lang=${lang}`)
  },
  routing: {
    getRoutingInfos: () =>
      request().get('/dialogflow/agent/routing'),
    updateRouting: (data: any) =>
      request().patch(`/dialogflow/agent/routing`, data),
    delete: (data: any) =>
      request().delete('/dialogflow/agent/routing', { data })
  },
  externalPackage: {
    get: () =>
      request().get('/dialogflow/agent/external_package'),
    update: (data: any) =>
      request().patch(`/dialogflow/agent/external_package`, data)
  },
  testCase: {
    save: (name: string, turns: any[]) =>
      API.common.create(PAGE_TYPE.TEST_CASE, {
        name,
        turns
      }),
    delete: (testCaseId: string) =>
      API.common.delete(PAGE_TYPE.TEST_CASE, { metaID: testCaseId }),
    testCaseRuns: () =>
      gqlReq(GraphQueries.testCaseRuns),
    updateRun: (testCaseId: string, turns: any[]) =>
      request().post(`/dialogflow/test_case/run`, {
        turns,
        test_case_id: testCaseId
      })
  },
  knowledge: {
    getAll: () =>
      request().get(`/dialogflow/knowledge_list`),
    getDeployed: () =>
      request().get(`/dialogflow/deployed_knowledge_agents`),
    getDetail: (id: string) =>
      request().get(`/dialogflow/knowledge?id=${id}`),
    update: (info: any) =>
      request().post(`/dialogflow/knowledge`, info),
    delete: (info: any) =>
      request().delete(`/dialogflow/knowledge`, { data: info }),
    deploy: () =>
      request().post(`/dialogflow/knowledge/deploy`),
    removeUpload: (knowledgeId: string, path: string) =>
      request().delete(`/dialogflow/knowledge/upload?knowledge_id=${knowledgeId}&file_path=${path}`)
  },
  messageTemplate: {
    getAll: () =>
      request().get(`/dialogflow/message_template`),
    add: (info: MessageTemplate) =>
      request().post(`/dialogflow/message_template`, info),
    update: (info: MessageTemplate) =>
      request().patch(`/dialogflow/message_template`, info),
    delete: (info: MessageTemplate) =>
      request().delete(`/dialogflow/message_template`, { data: info })
  },
  channelConfigMeta: {
    getAll: () =>
      request().get(`/dialogflow/channel_config_meta`),
    add: (info: ChannelConfigMeta) =>
      request().post(`/dialogflow/channel_config_meta`, info),
    update: (info: ChannelConfigMeta) =>
      request().patch(`/dialogflow/channel_config_meta`, info),
    delete: (info: ChannelConfigMeta) =>
      request().delete(`/dialogflow/channel_config_meta`, { data: info })
  }
};

function request(params?: {
  baseURL?: string;
  userId?: string;
  orgId?: string;
  orgName?: string;
  agentId?: string;
  agentName?: string;
  agentLang?: string;
}) {
  const locale = getLocale();

  if (params) {
    const req = axios.create({
      baseURL: BaseURL,
      headers: {
        userID: params.userId || getUserId(),
        orgID: params.orgId || getOrgId(),
        orgName: params.orgName || getOrgNameFromUrl() || '',
        agentID: params.agentId || getAgentId() || '',
        agentName: params.agentName || getAgentNameFromUrl() || '',
        agentLang: params.agentLang || getAgentLangFromUrl() || '',
        locale: locale && locale.localeStr,
        layerID: getLayerIdFromUrl(),
        layerType: getLayerTypeFromUrl()
      }
    });

    req.defaults.headers.post['Content-Type'] = 'application/json';
    req.defaults.withCredentials = true;
    req.interceptors.response.use(
      respResolve,
      respReject
    );

    return req;
  }

  axios.defaults.headers.userID = getUserId();
  axios.defaults.headers.agentID = getAgentId() || '';
  axios.defaults.headers.orgID = getOrgId() || '';
  axios.defaults.headers.orgName = getOrgNameFromUrl() || '';
  axios.defaults.headers.agentName = getAgentNameFromUrl() || '';
  axios.defaults.headers.agentLang = getAgentLangFromUrl() || '';
  axios.defaults.headers.layerID = getLayerIdFromUrl() || '';
  axios.defaults.headers.layerType = getLayerTypeFromUrl() || '';

  if (locale) {
    axios.defaults.headers.locale = locale.localeStr;
  }

  return axios;
}

/**
 * 将 server 发来的 null 转化为 undefined
 * @param data server result
 */
function formatRes(data: any) {
  if (typeof data === 'object') {
    Object.keys(data).forEach((key: any) => {
      if (data[key] === null) {
        data[key] = undefined;
      } else {
        formatRes(data[key]);
      }
    });
  }
}

function get3rdLoginRedirect() {
  if (window.location.pathname.endsWith('copilot_login')) {
    return `?redirect=${encodeURIComponent(`/copilot`)}`;
  }

  return '';
}
